import { useState, useRef, useContext, useCallback, startTransition } from 'react';
import { getRandomId } from '../../../Utils/baseComponentHelper.js';
import { FieldValid, FieldLoading } from '../Validation/types.js';
import { isEqualUndefinedOrEmpty } from '../../../../utils/js.js';
import FormContext from '../../Form/FormContext.js';
import { getAsyncInputStateByValidations } from '../Validation/v1Validations.js';
import { isValidationChanged, mergeValidationStates } from '../Validation/validations.js';

function isValueValidationChanged(newValue, oldValue, newValidation, oldValidation, isEqual) {
    if (!isEqual(newValue, oldValue))
        return true;
    if (isValidationChanged(newValidation, oldValidation))
        return true;
    return false;
}
function createValidationSetterIfChanged(newValidation) {
    return (s) => {
        if (isValidationChanged(s, newValidation)) {
            return newValidation;
        }
        return s;
    };
}
function useAsyncValidationHandler() {
    const currentValidationRef = useRef();
    const handleAsyncValidation = useCallback((validationResult, onValidationChange) => {
        const loadingValidation = FieldLoading;
        onValidationChange(loadingValidation);
        const currentValidation = getRandomId();
        currentValidationRef.current = currentValidation;
        validationResult.then((validationState) => {
            if (currentValidationRef.current !== currentValidation) {
                return;
            }
            onValidationChange(validationState);
        });
    }, []);
    return handleAsyncValidation;
}
function createSetTouchedIfValueIsDefinedCallback(isEqual, newValue, setTouched) {
    return () => {
        if (!isEqual(newValue, undefined)) {
            setTouched(true);
        }
    };
}
class ValidationFieldState {
    constructor() {
        this.localValidation = FieldValid;
        this.formValidation = FieldValid;
        this.isEqual = () => false;
        this.onValidationChange = () => { };
    }
    changeFormValidation(newValidation, isOnInitialization, callback) {
        if (isValidationChanged(newValidation, this.formValidation)) {
            this.formValidation = newValidation;
            this.onValidationChange(this.value, mergeValidationStates(this.formValidation, this.localValidation), isOnInitialization);
            callback();
        }
    }
    changeLocalValidation(newValue, newValidation, isOnInitialization) {
        if (this.isValueValidationChanged(newValue, newValidation)) {
            this.formValidation = FieldValid; // resetto la validazione della form
            this.localValidation = newValidation;
            this.value = newValue;
            this.onValidationChange(this.value, mergeValidationStates(this.formValidation, this.localValidation), isOnInitialization);
        }
    }
    isValueValidationChanged(newValue, newValidation) {
        return isValueValidationChanged(newValue, this.value, newValidation, this.localValidation, this.isEqual);
    }
    isValueChanged(newValue) {
        return isValueValidationChanged(newValue, this.value, this.localValidation, this.localValidation, this.isEqual);
    }
}
/**
 * Ritorna lo stato corrente del componente in base alle validazioni e allo stato controllato dall'utente
 * @param validationRules regole di validazione
 * @returns stato, messaggio di stato e callback al cambiamento del valore
 */
function useComponentStateWithValidations(fieldName, notifyValueChange, validationRules, isEqual = isEqualUndefinedOrEmpty) {
    const [touched, setTouched] = useState(false);
    const [validation, setValidation] = useState(FieldValid);
    const validationStateRef = useRef(new ValidationFieldState());
    validationStateRef.current.isEqual = isEqual;
    validationStateRef.current.onValidationChange = (value, val, isOnInitialization) => {
        setValidation(createValidationSetterIfChanged(val));
        notifyValueChange(value, val, isOnInitialization);
    };
    const handleAsyncValidation = useAsyncValidationHandler();
    const formContext = useContext(FormContext);
    const setFormValidation = useCallback((newValue, newValidation, isOnInitialization) => {
        validationStateRef.current.changeFormValidation(newValidation, isOnInitialization, createSetTouchedIfValueIsDefinedCallback(isEqual, newValue, setTouched));
    }, [isEqual]);
    const updateState = useCallback((value, isOnInitialization) => {
        startTransition(() => {
            function changeValidationByUpdate(newValidation) {
                validationStateRef.current.changeLocalValidation(value, newValidation, isOnInitialization);
            }
            const validationResult = getAsyncInputStateByValidations(validationRules, value);
            if (validationResult instanceof Promise) {
                handleAsyncValidation(validationResult, changeValidationByUpdate);
            }
            else {
                changeValidationByUpdate(validationResult);
            }
            if (fieldName) {
                formContext.validateByMultiField(fieldName, value, isOnInitialization);
            }
        });
    }, [fieldName, formContext, handleAsyncValidation, validationRules]);
    return [
        validation === null || validation === void 0 ? void 0 : validation.state,
        validation === null || validation === void 0 ? void 0 : validation.stateMessage,
        touched ? validation === null || validation === void 0 ? void 0 : validation.state : undefined,
        touched ? validation === null || validation === void 0 ? void 0 : validation.stateMessage : undefined,
        updateState,
        setTouched,
        setFormValidation,
    ];
}

export { useComponentStateWithValidations as default };
